import {
  createNativeProperty,
  readCreatedProperty,
  validNativeName,
  type PropertyCreateBody,
} from '@/api/core/native-property-create';
import { UnsavedGuard } from '@/components/unsaved-guard';
import {
  Button,
  DirtyConfirmDialog,
  FormField,
  Input,
  useDirtyConfirm,
} from '@mgrvtechgroup/admin-ds';
import { PlusIcon } from 'lucide-react';
import { useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import { commandOwner, epoch } from './presentation/command-owner';
import { useCurrentCommand } from './presentation/use-current-command';
import type { PropertyRuntime } from './property-queries';
/**
 * 지점 등록. `triggerSlot`을 주면 "지점 등록" 버튼만 그 자리(예: 페이지 헤더 우측 액션)에 그리고,
 * 입력 폼·결과 안내는 이 컴포넌트가 놓인 자리에 그대로 둔다. 슬롯이 없으면 버튼도 폼 자리에 그린다.
 */
export function NativePropertyCreate({
  runtime,
  triggerSlot,
}: {
  runtime: PropertyRuntime;
  triggerSlot?: HTMLElement | null;
}) {
  const lease = useSyncExternalStore(
    (l) => runtime.session.subscribe(l),
    () => epoch(runtime),
  );
  return <PropertyCreateForm key={lease} runtime={runtime} triggerSlot={triggerSlot} />;
}
function PropertyCreateForm({
  runtime,
  triggerSlot,
}: {
  runtime: PropertyRuntime;
  triggerSlot?: HTMLElement | null;
}) {
  const scope = 'native-property-create';
  const owner = commandOwner(runtime, scope);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [actorChanged, setActorChanged] = useState(false);
  const cmd = useCurrentCommand({
    runtime,
    scope,
    onActorChanged: () => {
      setName('');
      setCode('');
      setOpen(false);
      setActorChanged(true);
    },
    bits: 3,
    read: () =>
      readCreatedProperty(runtime.client, owner.attempt?.body as PropertyCreateBody | undefined),
    write: (body: PropertyCreateBody) => createNativeProperty(runtime.client, body),
    validate: () => Promise.resolve(),
  });
  const dirtyGuard = useDirtyConfirm(!!name || !!code);
  if (!cmd.canWrite) return null;
  const locked = cmd.busy || !!cmd.attempt;
  const trigger = (
    <Button
      variant="outline"
      onClick={() => {
        setActorChanged(false);
        setOpen(true);
      }}
    >
      <PlusIcon aria-hidden />
      지점 등록
    </Button>
  );
  // 버튼이 슬롯으로 나가 있으면 닫힌 동안 이 자리에는 보여 줄 것이 없다 — 빈 영역·랜드마크를 남기지 않는다.
  const showPanel = open || actorChanged || !triggerSlot;
  return (
    <section
      className={
        showPanel ? 'shrink-0 space-y-3 border-b px-[var(--layout-gutter)] py-4' : undefined
      }
      aria-label={showPanel ? '지점 등록' : undefined}
    >
      <DirtyConfirmDialog
        open={dirtyGuard.confirmOpen}
        onOpenChange={(next) => {
          if (!next) dirtyGuard.onCancel();
        }}
        onConfirm={dirtyGuard.onConfirm}
      />
      <UnsavedGuard
        includeSearch
        when={open && !cmd.success && (!!name || !!code || !!cmd.attempt)}
      />
      {actorChanged && (
        <p role="alert">
          작업자가 변경되어 이전 입력을 닫았습니다. 새 작업자가 다시 입력해 주세요.
        </p>
      )}
      {open &&
        (cmd.success && cmd.current ? (
          <div role="status">
            <p>지점 등록 후 현재 정보를 확인했습니다. 코드: {cmd.current.code}</p>
            <Link className="underline" to={`/properties/${cmd.current.id}`}>
              {cmd.current.name} 상세 보기
            </Link>
          </div>
        ) : (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (!cmd.attempt && (!validNativeName(name) || !validNativeName(code))) return;
              void cmd.submit({ command_id: crypto.randomUUID(), name, code });
            }}
          >
            <p className="body-sm">
              지점 이름과 코드를 확인한 뒤 등록하세요. 신청은 꺼진 상태로 생성됩니다.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <FormField label="지점 이름" htmlFor="native-property-name">
                <Input
                  id="native-property-name"
                  value={name}
                  disabled={locked}
                  onChange={(e) => {
                    setName(e.target.value);
                  }}
                />
              </FormField>
              <FormField label="지점 코드" htmlFor="native-property-code">
                <Input
                  id="native-property-code"
                  value={code}
                  disabled={locked}
                  onChange={(e) => {
                    setCode(e.target.value);
                  }}
                />
              </FormField>
            </div>
            {cmd.error && <p role="alert">{cmd.error}</p>}
            <Button
              type="submit"
              disabled={
                cmd.busy || (!cmd.attempt && (!validNativeName(name) || !validNativeName(code)))
              }
            >
              {cmd.attempt?.accepted
                ? '등록 결과 다시 조회'
                : cmd.attempt
                  ? '원래 요청 다시 확인'
                  : '등록 확인'}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={locked}
              onClick={() => {
                dirtyGuard.guardedClose(() => {
                  setName('');
                  setCode('');
                  setOpen(false);
                });
              }}
            >
              취소
            </Button>
          </form>
        ))}
      {!open && (triggerSlot ? createPortal(trigger, triggerSlot) : trigger)}
    </section>
  );
}
